import React, { useEffect, useState } from "react";
import { useApp } from "../contexts/AppContext";
import {
  Users,
  Clock,
  IndianRupee,
  AlertTriangle,
  LayoutGrid,
  List,
  CheckCircle2,
  Banknote,
  Smartphone,
} from "lucide-react";
import { format } from "date-fns";
import {
  server_post_data,
  get_funzone_dashboard,
  extend_funzone_session,
  release_funzone_billing,
  delete_funzone_billing,
} from "../ServiceConnection/serviceconnection.js";

/* ── Helpers ───────────────────────────────────── */

const elapsed = (t) => (Date.now() - new Date(t).getTime()) / 60000;

const timer = (t) => {
  const s = Math.floor((Date.now() - new Date(t).getTime()) / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return [h, m, sec].map((n) => String(n).padStart(2, "0")).join(":");
};

const statusOf = (ticket, config) => {
  if (!config) return ticket.status?.toLowerCase() || "green";

  const now = Date.now();
  const start = new Date(ticket.entryTime).getTime();
  const elapsedMin = Math.floor((now - start) / (1000 * 60));

  const greenLimit = config.green_until_minutes;
  const orangeLimit = config.orange_until_minutes;
  const redLimit = config.red_after_minutes;

  if (elapsedMin >= redLimit) return "red";
  if (elapsedMin >= greenLimit) return "orange";
  return "green";
};

const cardCls = {
  green: "border-green-200 bg-green-50",
  orange: "border-orange-200 bg-orange-50",
  red: "border-red-200 bg-red-50 animate-pulse-gentle",
};
const badgeCls = {
  green: "bg-green-500 text-white",
  orange: "bg-orange-500 text-white",
  red: "bg-red-500 text-white",
};

const Badge = ({ status }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold ${badgeCls[status]}`}
  >
    {status === "red" && <AlertTriangle size={11} />}
    {status.toUpperCase()}
  </span>
);

/* ── Main Component ────────────────────────────── */

export default function DashboardPage() {
  const { settings } = useApp();

  const [, tick] = useState(0);

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteBookingId, setDeleteBookingId] = useState(null);

  const [view, setView] = useState("card");
  const [tab, setTab] = useState("active");

  const [dashboardSummary, setDashboardSummary] = useState({
    active_kids: 0,
    todays_tickets: 0,
    todays_revenue: 0,
  });
  const [apiBookings, setApiBookings] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [todaysBookings, setTodaysBookings] = useState([]);

  const [extendModal, setExtendModal] = useState(false);
  const [extendMinutes, setExtendMinutes] = useState("");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [extendLoading, setExtendLoading] = useState(false);
  const [extendPayment, setExtendPayment] = useState("cash");
  const [releaseLoading, setReleaseLoading] = useState(null);
  const [config, setConfig] = useState(null);

  // 1-second tick for live timers
  useEffect(() => {
    const id = setInterval(() => tick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  /* ── FETCH DASHBOARD ── */
  const fetchDashboard = async () => {
    try {
      const response = await server_post_data(
        get_funzone_dashboard,
        new FormData(),
      );

      if (response.data.status === 1) {
        setDashboardSummary(response.data.summary);
        setApiBookings(response.data.active_bookings || []);
        setConfig(response.data.config_data);

        const completedMapped = (response.data.completed_bookings || [])
          .filter((b) => {
            if (!b.release_time) return false;
            const releasedAt = new Date(b.release_time).getTime();
            const now = Date.now();
            return now - releasedAt < 24 * 60 * 60 * 1000;
          })
          .map((b) => ({
            id: b.billing_id,
            kidName: b.kid_name,
            parentMobile: b.parent_mobile,
            numberOfKids: b.no_of_kids,
            duration: b.duration_minutes,
            entryTime: b.check_in_time,
            exitTime: b.release_time,
            paymentMode: b.payment_mode,
            totalAmount: b.total_amount,
          }));
        setCompleted(completedMapped);

        const todaysMapped = (response.data.todays_bookings || []).map((b) => ({
          id: b.billing_id,
          kidName: b.kid_name,
          parentMobile: b.parent_mobile,
          numberOfKids: b.no_of_kids,
          duration: b.duration_minutes,
          extended_minutes: b.extended_minutes || 0,
          entryTime: b.check_in_time,
          exitTime: b.release_time || null,
          paymentMode: b.payment_mode,
          totalAmount: b.total_amount,
          status: b.status,
        }));
        setTodaysBookings(todaysMapped);
      }
    } catch (error) {
      console.log("Dashboard API Error:", error);
    }
  };

  useEffect(() => {
    fetchDashboard();
    const interval = setInterval(fetchDashboard, 10000);
    return () => clearInterval(interval);
  }, []);

  /* ── MAP ACTIVE BOOKINGS ── */
  const active = apiBookings.map((b) => ({
    id: b.billing_id,
    kidName: b.kid_name,
    parentMobile: b.parent_mobile,
    numberOfKids: b.no_of_kids,
    duration: b.duration_minutes,
    extended_minutes: b.extended_minutes || 0,
    entryTime: b.check_in_time,
    endTime: b.end_time,
    paymentMode: b.payment_mode,
    totalAmount: b.total_amount,
    status: b.status,
    isActive: true,
  }));

  /* ── RELEASE TICKET ── */
  const handleRelease = async (billingId) => {
    setReleaseLoading(billingId);
    try {
      const fd = new FormData();
      fd.append("billing_id", billingId);
      fd.append("changed_by", localStorage.getItem("admin_name") || "ADMIN");

      const response = await server_post_data(release_funzone_billing, fd);
      if (response.data.status === 1) {
        fetchDashboard();
      }
    } catch (error) {
      console.log("Release API Error:", error);
    } finally {
      setReleaseLoading(null);
    }
  };

  const openDeleteModal = (billingId) => {
    setDeleteBookingId(billingId);
    setDeleteReason("");
    setDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setDeleteModal(false);
    setDeleteReason("");
    setDeleteBookingId(null);
  };

  const handleDelete = async () => {
    if (!deleteReason.trim()) {
      alert("Delete reason is required");
      return;
    }

    try {
      const fd = new FormData();
      fd.append("billing_id", deleteBookingId);
      fd.append("delete_reason", deleteReason);
      fd.append("deleted_by", localStorage.getItem("admin_name") || "ADMIN");

      const response = await server_post_data(delete_funzone_billing, fd);

      if (response.data.status === 1) {
        closeDeleteModal();
        fetchDashboard();
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.log("Delete Error:", error);
    }
  };

  /* ── EXTEND SESSION ── */
  const openExtendPopup = (booking) => {
    setSelectedBooking(booking);
    setExtendMinutes("");
    setExtendModal(true);
  };
  const closeExtendPopup = () => {
    setExtendModal(false);
    setExtendMinutes("");
    setSelectedBooking(null);
  };

  const extendBooking = async () => {
    if (!extendMinutes || Number(extendMinutes) <= 0) return;

    setExtendLoading(true);
    try {
      const fd = new FormData();
      fd.append("billing_id", selectedBooking.id);
      fd.append("extended_minutes", extendMinutes);
      fd.append("changed_by", localStorage.getItem("admin_name") || "ADMIN");

      const response = await server_post_data(extend_funzone_session, fd);

      if (response.data.status === 1) {
        closeExtendPopup();
        fetchDashboard();
      }
    } catch (error) {
      console.log("Extend API Error:", error);
    } finally {
      setExtendLoading(false);
    }
  };

  /* ── TAB BUTTON ── */
  const TabBtn = ({ name, label, Icon, count }) => (
    <button
      onClick={() => setTab(name)}
      className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold transition-colors ${
        tab === name
          ? "bg-purple-600 text-white"
          : "bg-white text-gray-600 hover:bg-gray-50"
      }`}
    >
      <Icon size={15} /> {label}
      {count > 0 && (
        <span
          className={`ml-1 rounded-full px-2 py-0.5 text-xs ${tab === name ? "bg-white/20 text-white" : "bg-gray-100 text-gray-700"}`}
        >
          {count}
        </span>
      )}
    </button>
  );

  /* ── RENDER ── */
  return (
    <div>
      <h1 className="font-display text-2xl font-bold text-gray-900">
        Dashboard
      </h1>
      <p className="mb-6 text-gray-500">Live tracking & overview</p>

      {/* Stats */}
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          {
            Icon: Users,
            label: "Active Kids",
            val: dashboardSummary.active_kids,
            bg: "bg-purple-100",
            ic: "text-purple-600",
          },
          {
            Icon: Clock,
            label: "Today's Tickets",
            val: dashboardSummary.todays_tickets,
            bg: "bg-blue-100",
            ic: "text-blue-600",
          },
          {
            Icon: IndianRupee,
            label: "Today's Revenue",
            val: `₹${dashboardSummary.todays_revenue}`,
            bg: "bg-green-100",
            ic: "text-green-600",
          },
        ].map(({ Icon, label, val, bg, ic }) => (
          <div
            key={label}
            className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
          >
            <div className="flex items-center gap-4">
              <div
                className={`flex h-11 w-11 items-center justify-center rounded-lg ${bg}`}
              >
                <Icon size={20} className={ic} />
              </div>
              <div>
                <p className="text-sm text-gray-500">{label}</p>
                <p className="font-display text-2xl font-bold text-gray-900">
                  {val}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex overflow-hidden rounded-lg border border-gray-200">
          <TabBtn
            name="active"
            label="Active Bookings"
            Icon={Clock}
            count={active.length}
          />
          <TabBtn
            name="completed"
            label="Completed"
            Icon={CheckCircle2}
            count={completed.length}
          />
          <TabBtn
            name="today"
            label="Today's All"
            Icon={Banknote}
            count={todaysBookings.length}
          />
        </div>
        {tab === "active" && active.length > 0 && (
          <div className="flex overflow-hidden rounded-lg border border-gray-200">
            {[
              ["card", "Cards", LayoutGrid],
              ["list", "List", List],
            ].map(([v, l, Ic]) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${view === v ? "bg-purple-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
              >
                <Ic size={15} /> {l}
              </button>
            ))}
          </div>
        )}
      </div>

      {tab === "active" ? (
        active.length === 0 ? (
          <EmptyState
            Icon={Clock}
            title="No active bookings"
            sub="Create a ticket from the Billing page"
          />
        ) : view === "card" ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {active.map((t) => {
              const s = statusOf(t, config);
              const isReleasing = releaseLoading === t.id;
              return (
                <div
                  key={t.id}
                  className={`rounded-xl border-2 p-4 ${cardCls[s]}`}
                >
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">{t.kidName}</h3>
                    <Badge status={s} />
                  </div>
                  <div className="mb-3 space-y-1 text-sm text-gray-500">
                    <p>📱 {t.parentMobile}</p>
                    <p>
                      👶 {t.numberOfKids} kid{t.numberOfKids > 1 ? "s" : ""}
                    </p>
                    <p>
                      ⏱️ {t.duration} min
                      {t.extended_minutes > 0 && (
                        <span className="text-green-600 font-semibold">
                          {" "}
                          + {t.extended_minutes} min
                        </span>
                      )}
                    </p>
                  </div>
                  <p className="mb-1 text-center font-mono text-2xl font-bold text-gray-900">
                    {timer(t.entryTime)}
                  </p>
                  <p className="mb-3 text-center text-xs text-gray-400">
                    elapsed
                  </p>
                  <div className="flex gap-3">
                    <button
                      disabled={isReleasing}
                      onClick={() => handleRelease(t.id)}
                      className="flex-1 rounded-lg bg-purple-600 text-white py-2 text-sm font-medium hover:bg-purple-700 transition-colors disabled:opacity-50"
                    >
                      {isReleasing ? "..." : "✅ Checkout"}
                    </button>
                    <button
                      onClick={() => openDeleteModal(t.id)}
                      className="rounded-lg bg-red-600 text-white px-3 py-1.5 text-xs font-medium hover:bg-red-700"
                    >
                      No show
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <TableView
            tickets={active}
            statusOf={(t) => statusOf(t, config)}
            timer={timer}
            cardCls={cardCls}
            onClose={handleRelease}
            onExtend={openExtendPopup}
            openDeleteModal={openDeleteModal}
            showActions
          />
        )
      ) : tab === "completed" ? (
        completed.length === 0 ? (
          <EmptyState
            Icon={CheckCircle2}
            title="No completed bookings yet"
            sub="Close an active ticket to see it here"
          />
        ) : (
          <TableView
            tickets={completed}
            showCompleted
            openDeleteModal={openDeleteModal}
          />
        )
      ) : todaysBookings.length === 0 ? (
        <EmptyState
          Icon={Banknote}
          title="No bookings today"
          sub="Today's bookings will appear here"
        />
      ) : (
        <TodaysTable
          tickets={todaysBookings}
          openDeleteModal={openDeleteModal}
        />
      )}

      {/* Extend Modal */}
      {extendModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-[400px] rounded-xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold mb-2">Extend Session</h2>
            <p className="text-sm text-gray-500 mb-4">
              {selectedBooking?.kidName}
            </p>
            <div className="flex gap-2 mb-4">
              {[10, 15, 30].map((m) => (
                <button
                  key={m}
                  onClick={() => setExtendMinutes(m)}
                  className={`flex-1 rounded-lg border py-2 text-sm transition-colors ${extendMinutes === m ? "border-purple-500 bg-purple-50 text-purple-700 font-bold" : "border-gray-300 hover:bg-gray-50"}`}
                >
                  +{m} min
                </button>
              ))}
            </div>
            <input
              type="number"
              placeholder="Custom minutes"
              value={extendMinutes}
              onChange={(e) => setExtendMinutes(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 mb-4"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={closeExtendPopup}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={extendBooking}
                className="rounded-lg bg-purple-600 text-white px-4 py-2 text-sm hover:bg-purple-700"
              >
                Extend Time
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl p-6 animate-fadeIn">
            <div className="mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                Reason for No-show
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Please provide a reason before marking this booking as a no-show.
              </p>
            </div>
            <textarea
              rows={4}
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Enter no show reason..."
              className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none"
            />
            <div className="mt-5 flex justify-end gap-3">
              <button
                onClick={closeDeleteModal}
                className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="rounded-xl bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                No show
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Empty State ── */
const EmptyState = ({ Icon, title, sub }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-12 text-center">
    <Icon size={40} className="mx-auto mb-3 text-gray-300" />
    <p className="text-gray-500">{title}</p>
    <p className="text-sm text-gray-400">{sub}</p>
  </div>
);

/* ── Table View (Active / Completed) ── */
const TableView = ({
  tickets,
  statusOf,
  timer,
  cardCls,
  onClose,
  onExtend,
  openDeleteModal,
  showActions,
  showCompleted,
}) => (
  <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-gray-200 bg-gray-50">
          <tr>
            {(showCompleted
              ? [
                  "Kid Name",
                  "Mobile",
                  "Kids",
                  "Entry",
                  "Exit",
                  "Duration",
                  "Amount",
                  "Payment",
                  "Action",
                ]
              : [
                  "Kid Name",
                  "Mobile",
                  "Kids",
                  "Session",
                  "Elapsed",
                  "Status",
                  "Amount",
                  "Payment",
                  "Action",
                ]
            ).map((h) => (
              <th
                key={h}
                className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {tickets.map((t) => {
            const s = showActions ? statusOf(t) : null;
            return (
              <tr key={t.id} className={s ? cardCls[s] : "hover:bg-gray-50"}>
                <td className="px-4 py-3 font-semibold text-gray-900">
                  {t.kidName}
                </td>
                <td className="px-4 py-3 text-gray-600">{t.parentMobile}</td>
                <td className="px-4 py-3 text-gray-600">{t.numberOfKids}</td>
                <td className="px-4 py-3 text-gray-600">
                  {t.duration} min
                  {t.extended_minutes > 0 && (
                    <span className="text-green-600 font-semibold">
                      {" "}
                      +{t.extended_minutes}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 font-mono font-bold text-gray-900">
                  {showCompleted
                    ? format(new Date(t.entryTime), "dd MMM, HH:mm")
                    : timer(t.entryTime)}
                </td>
                <td className="px-4 py-3">
                  {showCompleted ? (
                    <span className="text-gray-600">
                      {format(new Date(t.exitTime), "dd MMM, HH:mm")}
                    </span>
                  ) : (
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold ${{ green: "bg-green-500 text-white", orange: "bg-orange-500 text-white", red: "bg-red-500 text-white" }[s]}`}
                    >
                      {s === "red" && <AlertTriangle size={11} />}{" "}
                      {s?.toUpperCase()}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 font-semibold text-gray-900">
                  ₹{t.totalAmount}
                </td>
                <td className="px-4 py-3">
                  <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${
                        t.paymentMode?.toLowerCase() === "upi"
                          ? "bg-purple-100 text-purple-700"
                          : t.paymentMode?.toLowerCase() === "cash"
                          ? "bg-blue-100 text-blue-700"
                          : t.paymentMode?.toLowerCase() === "split"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                    {t.paymentMode?.toUpperCase()}
                  </span>
                </td>
                {showCompleted && (
                  <td className="px-4 py-3">
                    <button
                      onClick={() => openDeleteModal(t.id)}
                      className="rounded-lg bg-red-600 text-white px-3 py-1.5 text-xs font-medium hover:bg-red-700"
                    >
                      No show
                    </button>
                  </td>
                )}
                {showActions && (
                  <td className="px-3 py-2 w-[210px]">
                    <div className="flex items-center justify-center gap-2 w-full">
                      <button
                        onClick={() => onClose(t.id)}
                        className="rounded-lg bg-purple-600 text-white px-3 py-1.5 text-xs font-medium hover:bg-purple-700"
                      >
                        ✅ Checkout
                      </button>
                      <button
                        onClick={() => openDeleteModal(t.id)}
                        className="rounded-lg bg-red-600 text-white px-3 py-1.5 text-xs font-medium hover:bg-red-700"
                      >
                        No show
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

/* ── Today's All Table ── */
const TodaysTable = ({ tickets, openDeleteModal }) => (
  <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-gray-200 bg-gray-50">
          <tr>
            {[
              "S.No",
              "Kid Name",
              "Mobile",
              "Kids",
              "Duration",
              "Entry",
              "Exit",
              "Amount",
              "Payment",
              "Action",
            ].map((h) => (
              <th
                key={h}
                className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {tickets.map((t, idx) => (
            <tr key={t.id} className="hover:bg-gray-50">
              <td className="px-4 py-3 text-gray-400 font-medium">{idx + 1}</td>
              <td className="px-4 py-3 font-semibold text-gray-900">
                {t.kidName}
              </td>
              <td className="px-4 py-3 text-gray-600">{t.parentMobile}</td>
              <td className="px-4 py-3 text-gray-600">{t.numberOfKids}</td>
              <td className="px-4 py-3 text-gray-600">
                {t.duration} min
                {t.extended_minutes > 0 && (
                  <span className="text-green-600 font-semibold">
                    {" "}
                    +{t.extended_minutes}
                  </span>
                )}
              </td>
              <td className="px-4 py-3 text-gray-600">
                {t.entryTime
                  ? format(new Date(t.entryTime), "dd MMM, HH:mm")
                  : "—"}
              </td>
              <td className="px-4 py-3">
                {t.exitTime ? (
                  <span className="text-gray-600">
                    {format(new Date(t.exitTime), "dd MMM, HH:mm")}
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold bg-green-100 text-green-700">
                    Active
                  </span>
                )}
              </td>
              <td className="px-4 py-3 font-semibold text-gray-900">
                ₹{t.totalAmount}
              </td>
              <td className="px-4 py-3">
                <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${
                      t.paymentMode?.toLowerCase() === "upi"
                        ? "bg-purple-100 text-purple-700"
                        : t.paymentMode?.toLowerCase() === "cash"
                        ? "bg-blue-100 text-blue-700"
                        : t.paymentMode?.toLowerCase() === "split"
                        ? "bg-amber-100 text-amber-700"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                  {t.paymentMode?.toUpperCase()}
                </span>
              </td>
              <td className="px-4 py-3">
                <button
                  onClick={() => openDeleteModal(t.id)}
                  className="rounded-lg bg-red-600 text-white px-3 py-1.5 text-xs font-medium hover:bg-red-700"
                >
                  No show
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);
